import { AbstractControl, ValidationErrors, ValidatorFn, FormGroup } from '@angular/forms';

/**
 * Same as angular's Validators.required, but takes a custom error message
 * @param errorMessage
 * @returns
 */
export function createRequiredValidator(errorMessage: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    return !value || value.length === 0 ? { required: true, message: errorMessage } : null;
  };
}

export class CmgValidators {
  static required(invalidValue: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => (+control.value === invalidValue ? { required: true } : null);
  }

  static requiredString(invalidValue: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => (control.value === invalidValue ? { required: true } : null);
  }

  static emailValidator(): ValidatorFn {
    // Must have text before and after @
    // Must include a dot . after the domain
    // Top-level domain must be at least 2 characters
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;

      if (!value) {
        return null; // don't validate empty value (use Validators.required for that)
      }

      const valid = emailRegex.test(value);
      return valid ? null : { cmgValidationError: 'Invalid email format' };
    };
  }

  static nonWhitespace(control: AbstractControl) {
    return (control.value || '').trim().length > 0 ? null : { whitespace: true };
  }

  static numberIsGreaterThanOrEqualTo(value: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined || control.value === '') {
        return { required: true };
      } else {
        return +control.value >= value ? null : { required: true };
      }
    };
  }

  static customValidator(): ValidatorFn {
    const message = 'Taxes';
    return (formGroup: FormGroup) => {
      formGroup.get('monthlyInsTaxHoa').setErrors({ required: true });
      return { cmgValidationError: message };
    };
  }

  static companyName() {
    const message = ' Company name is Required';
    return (): { [key: string]: any } | null => {
      const isValid = false;
      return isValid ? null : { cmgValidationError: message };
    };
  }

  static commaSeperatedAge() {
    let message = 'Dependent Ages are not in comma seperated format. Example: "14, 20".';
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      let isValid = false;
      if (value === null || value === undefined || value.trim() === '') {
        isValid = true;
      } else {
        const items = value.split(',');
        //  Valid age check
        if (items.some((n) => Number(n) < 0 || Number(n) > 300)) {
          message = 'One of the Dependent Ages is not valid.';
        } else if (items.some((n) => isNaN(Number(n)) || !Number.isInteger(Number(n)))) {
          message = 'One of the Dependent Ages is not seperated by a comma.';
        } else {
          isValid = true;
        }
      }
      return isValid ? null : { cmgValidationError: message };
    };
  }

  /** Returns error if testValue is not true. */
  static isTrue(testValue: boolean, errorMessage?: string): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return testValue || !!control.value ? null : { cmgValidationError: errorMessage };
    };
  }

  static requiredBoolean(control: AbstractControl): ValidationErrors | null {
    if (control.value === true || control.value === false) {
      return null;
    }
    return { required: true };
  }

  static checkboxRequired(minRequired = 1): ValidatorFn {
    return function validate(formGroup: FormGroup) {
      let checked = 0;

      Object.keys(formGroup.controls).forEach((key) => {
        const control = formGroup.controls[key];
        if (control.value === true) {
          checked++;
        }
      });

      if (checked < minRequired) {
        return {
          requireCheckboxesToBeChecked: true,
        };
      }

      return null;
    };
  }

  /**
   * Performs validation logic via an external source (i.e. a callback function).
   * @param message The error message to display.
   * @param validationCallbackFn The callback function that determines if value is valid.
   * @returns Valid: `null`. Invalid: `{cmgValidationError: {{ message }}}`.
   */
  static callbackValidation(message: string, validationCallbackFn: (control: AbstractControl) => boolean) {
    return (control: AbstractControl): { [key: string]: string } | null => {
      const isValid = validationCallbackFn(control);
      return isValid ? null : { cmgValidationError: message };
    };
  }

  static createRangeValidator(min: number, max: number, cmgValidationError: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control?.value || control.value < min || control.value > max) {
        return { cmgValidationError };
      }

      return null;
    };
  }

  static arrayLengthValidator(minLength: number, maxLength?: number, cmgValidationError?: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!Array.isArray(value) || value?.length < minLength || (maxLength && value?.length > maxLength)) {
        return { cmgValidationError: cmgValidationError || true };
      }
      return null;
    };
  }
}
